import { FormEvent, useEffect, useMemo, useState } from "react"
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query"
import { Kontakt, KontaktInput, createKontakt, fetchKontakte, updateKontakt } from "@/lib/api"
import { EmployeePicker } from "@/components/EmployeePicker"
import { SkeletonTabelle } from "@/components/Skeleton"
import { EmptyState } from "@/components/EmptyState"
import { useToast } from "@/components/Toast"
import { Button, FieldError, Input, Select, TableWrapper, TD_CLASS, TH_CLASS, TR_CLASS } from "@/components/ui"
import {
  Sheet,
  SheetContent,
  SheetFooter,
  SheetHeader,
  SheetTitle,
  SheetDescription,
} from "@/components/ui/sheet"

const LEER: KontaktInput = { nachname: "", vorname: "", anrede: "" }

/** Kontakte-Übersicht + Anlegen/Bearbeiten im Sheet — AP-2.5. */
export function KontaktePage() {
  useEffect(() => {
    document.title = "Kontakte — RMNC Stammdaten"
  }, [])

  const queryClient = useQueryClient()
  const toast = useToast()
  const [form, setForm] = useState<KontaktInput>(LEER)
  const [bearbeiteId, setBearbeiteId] = useState<string | null>(null)
  const [nachnameFehler, setNachnameFehler] = useState<string | null>(null)
  const [suche, setSuche] = useState("")
  const [zeigeSheet, setZeigeSheet] = useState(false)

  const { data, isLoading, isError, refetch } = useQuery({
    queryKey: ["kontakte"],
    queryFn: () => fetchKontakte(),
  })

  const istEdit = bearbeiteId != null

  function oeffneNeu() {
    setBearbeiteId(null)
    setForm(LEER)
    setNachnameFehler(null)
    setZeigeSheet(true)
  }

  function oeffneBearbeiten(k: Kontakt) {
    setBearbeiteId(k.id)
    setForm({
      nachname: k.nachname,
      vorname: k.vorname,
      anrede: k.anrede,
      employeeId: k.employeeId,
    })
    setNachnameFehler(null)
    setZeigeSheet(true)
  }

  const anlegen = useMutation({
    mutationFn: (payload: KontaktInput) => createKontakt(payload),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["kontakte"] })
      setForm(LEER)
      setZeigeSheet(false)
      toast.show("Kontakt angelegt.", "success")
    },
    onError: (err: unknown) => {
      const detail =
        (err as { response?: { data?: { detail?: string } } })?.response?.data?.detail
      toast.show(detail ?? "Kontakt konnte nicht angelegt werden.", "error")
    },
  })

  const aktualisieren = useMutation({
    mutationFn: (payload: KontaktInput) => updateKontakt(bearbeiteId!, payload),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["kontakte"] })
      setZeigeSheet(false)
      toast.show("Kontakt aktualisiert.", "success")
    },
    onError: (err: unknown) => {
      const detail =
        (err as { response?: { data?: { detail?: string } } })?.response?.data?.detail
      toast.show(detail ?? "Kontakt konnte nicht aktualisiert werden.", "error")
    },
  })

  const speichern = istEdit ? aktualisieren : anlegen

  function onSubmit(e: FormEvent) {
    e.preventDefault()
    if (!form.nachname.trim()) {
      setNachnameFehler("Nachname ist erforderlich.")
      document.getElementById("kontakt-nachname")?.focus()
      return
    }
    setNachnameFehler(null)
    speichern.mutate(form)
  }

  const gefiltert = useMemo(() => {
    const q = suche.trim().toLocaleLowerCase("de")
    if (!q) return data ?? []
    return (data ?? []).filter((k) => {
      const name = `${k.vorname ?? ""} ${k.nachname}`.toLocaleLowerCase("de")
      return name.includes(q) || (k.employeeAnzeigename ?? "").toLocaleLowerCase("de").includes(q)
    })
  }, [data, suche])

  return (
    <div>
      <header className="mb-6 flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-semibold text-slate-900">Kontakte</h1>
          <p className="text-sm text-slate-600">
            Ansprechpartner (Ninox `K`) inkl. Funktionen/Rufnummern/E-Mails — AP-2.5.
          </p>
        </div>
        <Button onClick={oeffneNeu}>+ Neuer Kontakt</Button>
      </header>

      <Sheet open={zeigeSheet} onOpenChange={setZeigeSheet}>
        <SheetContent sheetKey="kontakt-anlegen" className="w-full overflow-y-auto sm:max-w-md">
          <SheetHeader>
            <SheetTitle>{istEdit ? "Kontakt bearbeiten" : "Neuer Kontakt"}</SheetTitle>
            <SheetDescription>
              {istEdit ? "Ansprechpartner bearbeiten." : "Ansprechpartner anlegen."}
            </SheetDescription>
          </SheetHeader>
          <form id="kontakt-neu-form" onSubmit={onSubmit} noValidate className="grid gap-4 py-4">
            <div className="space-y-1.5">
              <label className="block text-xs font-medium text-slate-700">Anrede</label>
              <Select
                value={form.anrede ?? ""}
                onChange={(e) => setForm({ ...form, anrede: e.target.value || null })}
              >
                <option value="">—</option>
                <option value="Herr">Herr</option>
                <option value="Frau">Frau</option>
              </Select>
            </div>
            <div className="space-y-1.5">
              <label className="block text-xs font-medium text-slate-700">Vorname</label>
              <Input
                value={form.vorname ?? ""}
                onChange={(e) => setForm({ ...form, vorname: e.target.value })}
              />
            </div>
            <div className="space-y-1.5">
              <label htmlFor="kontakt-nachname" className="block text-xs font-medium text-slate-700">
                Nachname *
              </label>
              <Input
                id="kontakt-nachname"
                required
                value={form.nachname}
                onChange={(e) => {
                  setForm({ ...form, nachname: e.target.value })
                  if (nachnameFehler) setNachnameFehler(null)
                }}
                invalid={!!nachnameFehler}
                aria-describedby={nachnameFehler ? "kontakt-nachname-fehler" : undefined}
              />
              <FieldError id="kontakt-nachname-fehler">{nachnameFehler}</FieldError>
            </div>
            <div>
              <EmployeePicker
                label="Interner Ansprechpartner"
                value={form.employeeId ?? null}
                onChange={(id: string | null) => setForm((f) => ({ ...f, employeeId: id }))}
              />
            </div>
          </form>
          <SheetFooter>
            <Button type="button" variant="secondary" onClick={() => setZeigeSheet(false)}>
              Abbrechen
            </Button>
            <Button type="submit" form="kontakt-neu-form" disabled={speichern.isPending}>
              {speichern.isPending ? "Speichert…" : istEdit ? "Speichern" : "Anlegen"}
            </Button>
          </SheetFooter>
        </SheetContent>
      </Sheet>

      {(data?.length ?? 0) > 10 && (
        <Input
          type="text"
          placeholder="Suche nach Name…"
          value={suche}
          onChange={(e) => setSuche(e.target.value)}
          className="mb-4 w-64"
        />
      )}

      {isError && (
        <div className="mb-4 flex items-center justify-between rounded-lg border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700">
          <span>Kontakte konnten nicht geladen werden.</span>
          <button
            type="button"
            onClick={() => refetch()}
            className="rounded-md font-medium underline focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-slate-400/60 focus-visible:ring-offset-1"
          >
            Erneut versuchen
          </button>
        </div>
      )}

      <TableWrapper>
        <table className="w-full text-sm">
          <thead>
            <tr>
              <th className={TH_CLASS}>Name</th>
              <th className={TH_CLASS}>Anrede</th>
              <th className={TH_CLASS}>Interner Ansprechpartner</th>
              <th className={TH_CLASS}>Status</th>
            </tr>
          </thead>
          {isLoading ? (
            <SkeletonTabelle zeilen={4} spalten={4} />
          ) : (
            <tbody>
              {gefiltert.map((k) => (
                <tr
                  key={k.id}
                  className={`${TR_CLASS} cursor-pointer`}
                  onClick={() => oeffneBearbeiten(k)}
                >
                  <td className={`${TD_CLASS} text-slate-900`}>
                    {[k.vorname, k.nachname].filter(Boolean).join(" ")}
                  </td>
                  <td className={TD_CLASS}>{k.anrede ?? "—"}</td>
                  <td className={TD_CLASS}>
                    {k.employeeAnzeigename ?? (
                      <>
                        {k.employeeId ? (
                          <code
                            className="rounded-md bg-slate-100 px-1.5 py-0.5 text-xs text-slate-500"
                            title="Mitarbeiter nicht (mehr) im Cache"
                          >
                            {k.employeeId}
                          </code>
                        ) : (
                          "—"
                        )}
                      </>
                    )}
                  </td>
                  <td className={TD_CLASS}>{k.aktiv ? "Aktiv" : "Inaktiv"}</td>
                </tr>
              ))}
            </tbody>
          )}
        </table>
      </TableWrapper>

      {!isLoading && !isError && (data?.length ?? 0) === 0 && (
        <EmptyState text="Noch keine Kontakte vorhanden." />
      )}
      {!isLoading && !isError && (data?.length ?? 0) > 0 && gefiltert.length === 0 && (
        <EmptyState text="Keine Kontakte gefunden." />
      )}
    </div>
  )
}
